import { useMemo, useState } from "react";
import { Layers, Search } from "lucide-react";
import { Checkbox, TextInput, TH, TD } from "../../../../components/ui";
import { TOOLS } from "../../../../mock/config.mock";
import SearchableSelect from "../../../../../components/ui/SearchableSelect";
import NoDataState from "../../../../../components/NoDataState";
import SectionShell from "../SectionShell";
import useSectionDraft from "../useSectionDraft";
import { CellNumber, CellPct, CellText, InlineSelect } from "../cells";

export const PRIMER_RULES = [
  { value: "ALWAYS", label: "Always" },
  { value: "NEW", label: "New construction only" },
  { value: "NEVER", label: "Never" },
];
const UNITS = ["SF", "LF", "EA"].map((u) => ({ value: u, label: u }));
const TOOL_OPTIONS = TOOLS.map((t) => ({ value: t, label: t }));

/** Shared search + group filter bar (also used by the Productivity matrix). */
export function SurfaceFilters({ search, onSearch, group, onGroup, groups, count, total }) {
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2">
      <div className="relative w-full max-w-xs">
        <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-text_muted" aria-hidden="true" />
        <TextInput value={search} onChange={(e) => onSearch(e.target.value)} placeholder="Search surfaces…" className="pl-8" aria-label="Search surfaces" />
      </div>
      <div className="w-56">
        <SearchableSelect
          options={groups.map((g) => ({ value: g, label: g }))}
          value={group}
          onChange={onGroup}
          placeholder="All groups"
          isClearable
        />
      </div>
      <span className="ml-auto text-[12px] text-text_muted">
        {count} of {total} surfaces
      </span>
    </div>
  );
}

export const useSurfaceFilter = (surfaces) => {
  const [search, setSearch] = useState("");
  const [group, setGroup] = useState("");
  const groups = useMemo(() => [...new Set((surfaces || []).map((s) => s.group))], [surfaces]);
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (surfaces || [])
      .map((s, index) => ({ s, index }))
      .filter(({ s }) => (!group || s.group === group) && (!q || s.name.toLowerCase().includes(q) || s.group.toLowerCase().includes(q)));
  }, [surfaces, search, group]);
  return { search, setSearch, group, setGroup, groups, rows };
};

export default function SurfacesSection({ config, readOnly, onSaved }) {
  const draft = useSectionDraft(config, "surfaces", onSaved, "Surfaces");
  const surfaces = draft.value;
  const filter = useSurfaceFilter(surfaces);

  const productOptions = useMemo(() => {
    const active = (config.products || []).filter((p) => p.active !== false);
    const opt = (p) => ({ value: p.id, label: `${p.line} – ${p.sheen}` });
    return {
      finish: [{ value: "", label: "— none —" }, ...active.filter((p) => p.type === "FINISH").map(opt)],
      primer: [{ value: "", label: "— none —" }, ...active.filter((p) => p.type === "PRIMER").map(opt)],
    };
  }, [config.products]);

  if (!surfaces) return null;
  const set = (index, key) => (v) => draft.update((d) => { d[index][key] = v; });

  return (
    <SectionShell
      title="Surfaces"
      subtitle="Each surface's defaults are applied to a takeoff line when it is mapped."
      icon={Layers}
      draft={draft}
      readOnly={readOnly}
    >
      <SurfaceFilters
        search={filter.search}
        onSearch={filter.setSearch}
        group={filter.group}
        onGroup={filter.setGroup}
        groups={filter.groups}
        count={filter.rows.length}
        total={surfaces.length}
      />

      {filter.rows.length === 0 ? (
        <NoDataState icon="search" title="No surfaces match" message="Try a different search term or clear the group filter." showBack={false} showRefresh={false} size="small" />
      ) : (
        <div className="max-h-[calc(100vh-330px)] overflow-auto rounded-[4px] border border-border">
          <table className="w-full border-separate border-spacing-0">
            <thead className="sticky top-0 z-20">
              <tr>
                <th className={`${TH} sticky left-0 z-30 min-w-[240px]`}>Surface</th>
                <th className={TH}>Group</th>
                <th className={TH}>Unit</th>
                <th className={`${TH} text-right`}>SF / unit</th>
                <th className={`${TH} text-right`}>Factor</th>
                <th className={TH}>Default tool</th>
                <th className={`${TH} text-right`}>Cut %</th>
                <th className={TH}>Primer rule</th>
                <th className={TH}>Default finish</th>
                <th className={TH}>Default primer</th>
                <th className={TH}>Coats</th>
                <th className={TH}>Subbed?</th>
                <th className={`${TH} text-right`}>Sub $/SF</th>
              </tr>
            </thead>
            <tbody>
              {filter.rows.map(({ s, index }) => (
                <tr key={s.id}>
                  <td className={`${TD} sticky left-0 z-10 bg-surface py-1`}>
                    <CellText value={s.name} onChange={set(index, "name")} disabled={readOnly} ariaLabel="Surface name" />
                  </td>
                  <td className={`${TD} text-text_secondary`}>{s.group}</td>
                  <td className={`${TD} py-1`}>
                    <InlineSelect value={s.unit} options={UNITS} onChange={set(index, "unit")} disabled={readOnly} ariaLabel={`${s.name} unit`} minWidth={70} />
                  </td>
                  <td className={`${TD} w-24 py-1`}>
                    <CellNumber value={s.sfPerUnit} onChange={set(index, "sfPerUnit")} disabled={readOnly} step="0.1" min={0} ariaLabel={`${s.name} SF per unit`} />
                  </td>
                  <td className={`${TD} w-24 py-1`}>
                    <CellNumber value={s.factor} onChange={set(index, "factor")} disabled={readOnly} step="0.05" min={0} ariaLabel={`${s.name} coverage factor`} />
                  </td>
                  <td className={`${TD} py-1`}>
                    <InlineSelect value={s.defaultTool} options={TOOL_OPTIONS} onChange={set(index, "defaultTool")} disabled={readOnly} ariaLabel={`${s.name} default tool`} minWidth={130} />
                  </td>
                  <td className={`${TD} w-24 py-1`}>
                    <CellPct value={s.cutPct} onChange={set(index, "cutPct")} disabled={readOnly} min={0} max={100} ariaLabel={`${s.name} cut percent`} />
                  </td>
                  <td className={`${TD} py-1`}>
                    <InlineSelect value={s.primerRule} options={PRIMER_RULES} onChange={set(index, "primerRule")} disabled={readOnly} ariaLabel={`${s.name} primer rule`} minWidth={170} />
                  </td>
                  <td className={`${TD} py-1`}>
                    <InlineSelect value={s.defaultFinishProductId} options={productOptions.finish} onChange={set(index, "defaultFinishProductId")} disabled={readOnly} ariaLabel={`${s.name} default finish`} minWidth={230} />
                  </td>
                  <td className={`${TD} py-1`}>
                    <InlineSelect value={s.defaultPrimerProductId} options={productOptions.primer} onChange={set(index, "defaultPrimerProductId")} disabled={readOnly} ariaLabel={`${s.name} default primer`} minWidth={210} />
                  </td>
                  <td className={`${TD} w-20 py-1`}>
                    <CellNumber value={s.defaultCoats} onChange={set(index, "defaultCoats")} disabled={readOnly} step="1" min={0} max={5} ariaLabel={`${s.name} default coats`} />
                  </td>
                  <td className={TD}>
                    <Checkbox checked={s.subbed} onChange={set(index, "subbed")} disabled={readOnly} aria-label={`${s.name} subcontracted`} />
                  </td>
                  <td className={`${TD} w-24 py-1`}>
                    {s.subbed ? (
                      <CellNumber value={s.subRatePerSf} onChange={set(index, "subRatePerSf")} disabled={readOnly} step="0.05" min={0} ariaLabel={`${s.name} sub rate per SF`} />
                    ) : (
                      <span className="block text-right text-text_muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionShell>
  );
}
